import { useState, useRef, useEffect, useMemo, memo } from "react";
import { useSwipeable } from 'react-swipeable';
import { MobileDateRangePicker, SingleInputDateRangeField  } from '@mui/x-date-pickers-pro';
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import { endOfDay, startOfDay } from "date-fns";
import { 
  Box,
  Grid,
  Typography,
  Divider,
  Badge,
  Fab,
  Fade,
  ToggleButtonGroup,
  ToggleButton,
  Slide,
  IconButton,
  InputAdornment,
  Stack
} from "@mui/material";
import { fMonthDayYearShortYear, fHoursMinutes, fDayOfWeek } from "../../../../utils/formatTime";
import Iconify from "../../../../reusable-components/iconify";
import { NotificationIdsChip, useNotificationIdsFilter } from "../../../../hooks/useNotificationIdsFilter";
import { postPinTicket, postSetTicketMessagesRead } from "../../../../api/tickets";
import { updateSingleTicket } from "../../../../redux/slices/tickets";
import { markMetricRemoved, markMetricAdded, NotificationMetricType } from "../../../../redux/slices/notifications";
import { useScopeCheck } from "../../../../reusable-components/scopes";
import TicketQuickSetRemindMe from "../ticket/TicketQuickSetRemindMe";
import { getUnreadData } from "../../../../redux/slices/dashboard";
import DebounceTextField from "../../../../reusable-components/debounce-text-field/DebounceTextField";
import TicketFilters from "./TicketFilters";
import { isValidDate } from "../../../../utils/validate";
import LoadingAnimation from "../../../../reusable-components/loader/LoadingAnimation";
import { useMobileListScrollRestore, useListStateRestore } from "../../../../hooks/useGridScrollRestore";

MobileTicketList.propTypes = {
  isTicketsLoaded: PropTypes.bool,
};

export default function MobileTicketList({isTicketsLoaded}) {

  const [showScroll, setShowScroll] = useState(false);
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialFilter = params.get("status") || "active";
  const [ticketsFilter, setTicketsFilter] = useState(initialFilter);
  const [openOptions, setOpenOptions] = useState(null);
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  const { matchesNotificationIds, isNotificationLoading } = useNotificationIdsFilter([NotificationMetricType.TicketsUnread]);
  const gridId = status === "complete" ? "mobile-completed-tickets" : "mobile-open-tickets";
  const { restoredState, listStateRef, saveListState } = useListStateRestore({ gridId });
  const [isSearchActive, setIsSearchActive] = useState(!!restoredState?.isSearchActive);
  const [isFilterActive, setIsFilterActive] = useState(!!restoredState?.isFilterActive);
  const [filters, setFilters] = useState(() => restoredState?.filters
    // the date range comes back from JSON as strings
    ? restoredState.filters.map(f => f.name === 'createdOn'
      ? {...f, value: Array.isArray(f.value) ? f.value.map(d => d ? new Date(d) : null) : [null, null]}
      : f)
    : [
      {name: "extraInfo", label: "Subject", value: ""},
      {name: "id", label: "Ticket #", value: ""},
      {name: "createdOn", label: "Date Added", value: [null, null]},
      {name: "facility", label: "Facility", value: ""},
      {name: "manager", label: "Manager", value: ""}
    ]);
  const [search, setSearch] = useState(restoredState?.search || "");
  const [appliedSearch, setAppliedSearch] = useState(restoredState?.search || "");
  // a restored search box shouldn't pop the keyboard; one the user opens should
  const skipSearchAutoFocus = useRef(!!restoredState?.isSearchActive);
  listStateRef.current = { isSearchActive, search, isFilterActive, filters };
  const isManager = useScopeCheck(["Facility-Manager"]);
  const scrollTimeout = useRef();
  const listRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: { completedTickets, activeTickets } } = useSelector((state) => state.tickets);
  
  const tickets = useMemo(
    () => status === "complete"
      ? completedTickets
      : (activeTickets || []).filter((ticket) => matchesNotificationIds(ticket.id, ...(ticket.subticketsUnread ?? []))),
    [status, completedTickets, activeTickets, matchesNotificationIds]
  );

  const { saveScrollAnchor } = useMobileListScrollRestore({
    gridId,
    scrollElementRef: listRef,
    ready: isTicketsLoaded && !!tickets?.length,
  });

  const sortedTickets = useMemo(() => [...tickets]?.sort((a, b) => {
    if (a.aboveLine && !b.aboveLine) return -1;
    if (!a.aboveLine && b.aboveLine) return 1;
    return new Date(b.createdOn) - new Date(a.createdOn);
  }), [completedTickets, activeTickets, status, tickets]);

  // Derived (not effect-set) so a restored search / filter is re-applied as soon
  // as the tickets reload, before the scroll restore runs against the list.
  // Search and the filter bar are mutually exclusive in the UI.
  const ticketsToDisplay = useMemo(() => {
    // split on any whitespace and drop empty terms; an empty term would match every ticket
    const searchTerms = appliedSearch.toLowerCase().split(/\s+/).filter(Boolean);
    if (searchTerms.length) {
      return sortedTickets.filter(
        ticket => {
          // search managers by name/email, not the raw array (whose toString is "[object Object]")
          const { facilityManagerAssignments, ...ticketFields } = ticket;
          // skip missing name/email so they don't become the text "null" / "undefined"
          const managerText = facilityManagerAssignments?.map(m => [m.manager, m.email].filter(Boolean).join(' ')).join(', ') ?? '';
          return [...Object.values(ticketFields), managerText].some(
            value => searchTerms.some(term => value?.toString()?.toLowerCase().includes(term))
          );
        }
      );
    }
    // a missing or invalid (half-typed) end counts as unset, so a half-picked range is open-ended;
    // the bounds are widened to whole days, so the start and end days are fully included
    const dateRange = filters.find(f => f.name === 'createdOn')?.value ?? [];
    const rangeStart = isValidDate(dateRange[0]) ? startOfDay(dateRange[0]) : null;
    const rangeEnd = isValidDate(dateRange[1]) ? endOfDay(dateRange[1]) : null;
    const areAllFiltersEmpty = filters.every(f => f.name === 'createdOn' ? !rangeStart && !rangeEnd : !f.value);
    if (areAllFiltersEmpty) return sortedTickets;
    return sortedTickets.filter(ticket => filters.every(
      (f) => {
        const ticketValue = ticket[f.name]?.toString()?.toLowerCase();
        const filterValue = f.value?.toString()?.toLowerCase();
        if (f.name !== 'createdOn' && !filterValue) return true;
        if (f.name === 'createdOn') {
          const createdOn = new Date(ticket.createdOn);
          return (!rangeStart || createdOn >= rangeStart) && (!rangeEnd || createdOn <= rangeEnd);
        }
        // match each assigned manager's name on its own, so emails and other managers can't produce a match
        if (f.name === 'manager') {
          return ticket.facilityManagerAssignments?.some(m => m.manager?.toLowerCase().includes(filterValue)) ?? false;
        }
        return ticketValue?.includes(filterValue);
      }
    ));
  }, [sortedTickets, appliedSearch, filters]);

  const handleScroll = () => {
    if (scrollTimeout.current) clearTimeout(scrollTimeout.current);

    scrollTimeout.current = setTimeout(() => {
      if (listRef?.current) {
        setShowScroll(listRef?.current.scrollTop > 100);
      }
    }, 50);
  };

  const handleScrollToTop = () => {
    if (listRef?.current) {
      listRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const checkIfDateIsToday = (date) => {
    const today = new Date();
    const ticketDate = new Date(date);
    return today.getDate() === ticketDate.getDate() &&
            today.getMonth() === ticketDate.getMonth() &&
            today.getFullYear() === ticketDate.getFullYear();
  }

  const checkIfDateIsThisWeek = (date) => {
    const today = new Date();
    const ticketDate = new Date(date);
    const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()));
    const lastDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay() + 6));
    return ticketDate >= firstDayOfWeek && ticketDate <= lastDayOfWeek;
  }

  const goToTicket = (ticketId) => {
    saveScrollAnchor();
    saveListState();
    const params = status === "complete" ? `?start=${searchParams.get('start') || ''}&end=${searchParams.get('end') || ''}` : '';
    navigate(`/dashboard/tickets/${ticketId}${params}`);
  }

  useEffect(() => {
    let filter = `?status=${ticketsFilter}`;
    if(ticketsFilter === "complete"){
      const startParam = searchParams.get("start");
      const endParam = searchParams.get("end");
      let dateRange;
      if(isValidDate(startParam) && isValidDate(endParam)) {
        filter += `&start=${startParam}&end=${endParam}`;
        dateRange = [new Date (startParam), new Date (endParam)];
      }
      else {
        const start = new Date(new Date().setDate(new Date().getDate() - 29));
        const end = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
        filter += `&start=${start.toISOString()}&end=${end.toISOString()}`
        dateRange = [start, end];
      }
      if(selectedDateRange[0] !== dateRange[0] || selectedDateRange[1] !== dateRange[1]) {
        setSelectedDateRange(dateRange);
      }
    }
    
    navigate(filter, { replace: true });
  }, [ticketsFilter]);

  const handlePinTicket = async (ticketId, action) => {
    try {
      const { data } = await postPinTicket({ ticketId });
      if (data) {
        enqueueSnackbar(`Ticket ${ticketId} ${action === 'pinned' ? 'pinned' : 'unpinned'} successfully`, { variant: 'success' });
        dispatch(updateSingleTicket(data));
      }
    } catch (error) {
      enqueueSnackbar(`Error: Could not ${action} ticket ${ticketId}`, { variant: 'error' });
    }
  };
  
  const handleDateRangeChange = (newValue) => {
    setSelectedDateRange(newValue);
  }

  const handleDateRangeClosed = () => {
    setIsDateRangePickerOpen(false);
    if(selectedDateRange[0] && selectedDateRange[1]) {
      const formattedStartDate = selectedDateRange[0]?.toISOString();
      const formattedEndDate = selectedDateRange[1]?.toISOString();
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('start', formattedStartDate);
      searchParams.set('end', formattedEndDate);
      const newUrl = `${location.pathname}?${searchParams.toString()}`;
      navigate(newUrl);
    }
  }

  const handleSearch = () => {
    setAppliedSearch(search);
  }

  const handleCloseSearch = () => {
    setIsSearchActive(false);
    setSearch("");
    setAppliedSearch("");
  }

  const goToOrder = (e, orderId) => {
    e.stopPropagation();
    navigate(`/dashboard/orders/${orderId}`);
  }

  const Row = memo(({ index, style }) => {
    const ticket = ticketsToDisplay[index];

    const handlers = useSwipeable({
      onSwipedRight: () => {
        if (!openOptions) setOpenOptions({ id: ticket.id, side: "left" });
        else if(openOptions) setOpenOptions(null);
      },
      onSwipedLeft: () => {
        if (!openOptions) setOpenOptions({ id: ticket.id, side: "right" });
        else if(openOptions) setOpenOptions(null);
      },
      trackMouse: true,
    });

    const handleMarkAsRead = async (ticketId, action) => {
      try {
        const response = await postSetTicketMessagesRead({ id: ticketId, markAsUnread: action });
        if (response?.status === 200) {
          dispatch(
            updateSingleTicket({
              id: ticketId,
              messageCount: action ? 1 : 0,
            })
          );
          dispatch(getUnreadData());
          if (action) {
            dispatch(markMetricAdded(NotificationMetricType.TicketsUnread, ticketId));
          } else {
            dispatch(markMetricRemoved(NotificationMetricType.TicketsUnread, ticketId));
          }
        }
      } catch (error) {
        enqueueSnackbar(`Error: Could not ${action} label to ticket ${ticketId}`, {
          variant: 'error',
        });
      }
    };

    const handleTicketClick = (ticketId) => {
      goToTicket(ticketId);
      handleMarkAsRead(ticketId, false);
    }

    const Slider = ({ ticket, side }) => (
      <Slide
        direction={side === "left" ? "right" : "left"}
        in={openOptions?.id === ticket?.id && openOptions?.side === side}
        mountOnEnter
        unmountOnExit
        sx={{
          backgroundColor: 'transparent',
          position: 'absolute',
          [side]: 0,
          zIndex: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {isManager && <>
            <Box sx={{
              backgroundColor: 'info.main',
              p: 1,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <TicketQuickSetRemindMe
                params={{ row: ticket }}
                unpinTicket={(date) => {
                  dispatch(updateSingleTicket({ ...ticket, boomerangOn: date, aboveLine: false }));
                  setOpenOptions(null);
                }}
                boomerangOnColor="white"
                boomerangOffColor="white"
              />
              <Typography sx={{display: 'block', color: '#ffff'}} variant="caption">Reminder</Typography>
            </Box>
            <Box sx={{p: 1, backgroundColor: 'text.disabled', height: '100%'}}>
              <IconButton
                color="white"
                onClick={() => {
                  setOpenOptions(null);
                  handlePinTicket(ticket.id, ticket.aboveLine ? 'unpinned' : 'pinned');
                }}
              >
                {ticket.aboveLine ? (
                  <Box>
                    <Iconify icon="mdi:pin" height={23} width={23}/>
                    <Typography sx={{display: 'block'}} variant="caption">Unpin</Typography>
                  </Box>
                ) : (
                  <Box>
                    <Iconify icon="mdi:pin-outline" height={23} width={23}/>
                    <Typography sx={{display: 'block'}} variant="caption">Pin</Typography>
                  </Box>
                )}
              </IconButton>
            </Box>
          </>}
          <Box sx={{p: 1, backgroundColor: 'secondary.main', height: '100%'}}>
            <IconButton
              onClick={() => {
                handleMarkAsRead(ticket.id, ticket.messageCount === 0);
                setOpenOptions(null);
              }}
            >
              {
                ticket.messageCount > 0 ?
                  <Box>
                    <Iconify icon="mdi:email" height={23} width={23} color="white"/>
                    <Typography sx={{display: 'block', color: "#ffff"}} variant="caption">Mark Read</Typography>
                  </Box> :
                  <Box>
                    <Iconify icon="mdi:email-mark-as-unread" height={23} width={23} color="white"/>
                    <Typography sx={{display: 'block', color: "#ffff"}} variant="caption">Mark Unread</Typography>
                  </Box>
              }
            </IconButton>
          </Box>
        </Box>
      </Slide>
    );

    return (
      <Box style={style} {...handlers} sx={{overflow: 'hidden'}}>
        {status !== "complete" && <>
          <Slider ticket={ticket} side="left"/>
          <Slider ticket={ticket} side="right"/>
        </>}
        <Grid
          container
          direction="row"
          sx={{
            display: 'flex',
            alignItems: 'start',
            justifyContent: 'space-between',
            borderLeft: ticket.importance === "High"
              ? (theme) => `5px solid ${theme.palette.error.main}`
              : `5px solid transparent`,
            px: '12px',
            py: '8px',
            borderTopRightRadius: '12px',
            borderBottomRightRadius: '12px',
            transform:
              openOptions?.id === ticket.id && openOptions?.side === "left" && status !== "complete"
                ? isManager ? 'translateX(235px)' : 'translateX(85px)'
                : openOptions?.id === ticket.id && openOptions?.side === "right" && status !== "complete"
                ? isManager ? 'translateX(-235px)' : 'translateX(-85px)'
                : 'none',
            transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
          }}
          spacing={0}
          onClick={() => handleTicketClick(ticket.id)}
        >
          <Grid item xs={2.5} sx={{display: 'flex', alignItems: 'center', position: 'relative'}}>
            <Box
              sx={{
                width: 55,
                height: 55,
                borderRadius: '50%',
                border: (theme) => `2px solid ${theme.palette.info.main}`,
                color: (theme) => theme.palette.info.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                fontSize: 12,
                flexShrink: 0,
                boxSizing: 'border-box',
                transition: 'margin-left 225ms cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {ticket.id}
            </Box>
          </Grid>
          <Grid item xs={6.5}>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{
                textAlign: 'left',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {ticket.extraInfo}
            </Typography>
            <Typography color="text.secondary" variant="subtitle2" sx={{ minWidth: 0, display: 'flex', alignItems: 'center' }}>
              <Box
                component="span"
                sx={{
                  fontWeight: 'bold',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                  maxWidth: '100%'
                }}
              >
                {ticket.facility}
              </Box>
              {ticket.ticketOrderId > 0 && (
                <Box component="span" sx={{ whiteSpace: 'nowrap', ml: 0.5 }}>
                  | <Box
                      component="span"
                      onClick={(e) => goToOrder(e, ticket.ticketOrderId)}
                      sx={{textDecoration: 'underline', cursor: 'pointer'}}
                    >{ticket.ticketOrderId}</Box>
                </Box>
              )}
            </Typography>
            <Typography color="text.secondary" variant="subtitle2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {ticket.lastMessage}
            </Typography>         
          </Grid>
          <Grid item xs={3} sx={{ textAlign: 'right' }}>
            <Typography variant="subtitle2" color="text.secondary">
              {checkIfDateIsToday(ticket.createdOn) ? fHoursMinutes(ticket.createdOn) :
               checkIfDateIsThisWeek(ticket.createdOn) ? fDayOfWeek(ticket.createdOn) :
               fMonthDayYearShortYear(ticket.createdOn)}
            </Typography>
            {ticket.messageCount > 0 && <Badge badgeContent={ticket.messageCount} color="secondary" sx={{mr: ticket.aboveLine ? 1 : 3}}/>}
            {ticket.aboveLine && <IconButton onClick={(event) => {
              event.stopPropagation();
              handlePinTicket(ticket.id, "unpinned");
            }}><Iconify icon="mdi:pin" color="secondary"/></IconButton>}
          </Grid>
        </Grid>
        {index < ticketsToDisplay.length - 1 && <Divider sx={{width: '100%', borderWidth: '1px', opacity: ticket.aboveLine ? 0.5 : ''}}/>}
      </Box>
    );
  });
  
  return <Box sx={{height: '100%', mt: 1}}>
    {isSearchActive ?
    <DebounceTextField
      value={search}
      debounceFunc={handleSearch}
      size="small"
      onChange={(e) => setSearch(e.target.value)}
      autoFocus={!skipSearchAutoFocus.current}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Iconify icon="mdi:search" sx={{ color: 'text.secondary', fontSize: 20 }} />
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <Iconify
              icon="mdi:close"
              sx={{ color: 'text.secondary', fontSize: 20 }}
              onClick={handleCloseSearch}
            />
          </InputAdornment>
        ),
      }}
      sx={{width: '100%', mb: 1}}
    /> :
    isFilterActive ? 
    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
      <TicketFilters
        filters={filters}
        setFilters={setFilters}
        tickets={tickets}
        setIsFilterActive={setIsFilterActive}
      />
    </Stack> :
    <Box sx={{display: 'flex', justifyContent: 'space-between', alignContent: 'center', mb: 1}}>
      <ToggleButtonGroup
        value={ticketsFilter}
        exclusive
        variant="contained"
        onChange={(val) => setTicketsFilter(val.target.value)}
        aria-label="text alignment"
        size="small"
        color="primary"
        sx={{ p: 0 }}
      >
        <ToggleButton value="active" aria-label="left aligned" size="small">
          Open Tickets
        </ToggleButton>
        <ToggleButton value="complete" aria-label="centered" size="small">
          Completed Tickets
        </ToggleButton>
      </ToggleButtonGroup>
      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
        <Iconify
          icon="mdi:filter-variant"
          sx={{ fontSize: 24, height: 24, width: 24, verticalAlign: 'middle', cursor: 'pointer', mr: 1 }}
          onClick={() => setIsFilterActive(true)}
        />
        <Iconify
          icon="mdi:search"
          sx={{ fontSize: 24, height: 24, width: 24, verticalAlign: 'middle', cursor: 'pointer' }}
          onClick={() => {
            skipSearchAutoFocus.current = false;
            setIsSearchActive(true);
          }}
        />
        {status === "complete" && (
          <>
            <MobileDateRangePicker
              value={selectedDateRange}
              onChange={handleDateRangeChange}
              open={isDateRangePickerOpen}
              onClose={handleDateRangeClosed}
              slots={{ field: SingleInputDateRangeField }}
              slotProps={{
                textField: {
                  sx: { display: 'none' },
                },
              }}
            />
            <IconButton onClick={() => setIsDateRangePickerOpen(true)} sx={{ p: 0.5, ml: 1, display: 'flex', alignItems: 'center' }}>
              <Iconify icon="mdi:calendar-range" sx={{ fontSize: 24, height: 24, width: 24, verticalAlign: 'middle' }} />
            </IconButton>
          </>
        )}
      </Box>
    </Box>}
    {status !== "complete" && <NotificationIdsChip label="tickets" />}
    {
      !isTicketsLoaded || (status !== "complete" && isNotificationLoading) ? (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          //48px to account for the height of the toggle button above
          height: 'calc(100% - 48px)'
        }}>
          <LoadingAnimation />
        </Box>
      ) :
      <>
        <Box
          sx={{borderRadius: '10px', height: '90%'}}
          style={{backgroundColor: 'white'}}
        >
          {!tickets?.length ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>No tickets</Box>
          ) : (
            <AutoSizer>
              {({ height, width }) => (
                <List
                  height={height || 0}
                  itemCount={ticketsToDisplay?.length || 0}
                  itemSize={77.5}
                  width={width}
                  onScroll={handleScroll}
                  outerRef={listRef}
                  itemKey={index => ticketsToDisplay[index].id}
                >
                  {Row}
                </List>
              )}
            </AutoSizer>
          )}
        </Box>
        <Fade in={showScroll}>
          <Fab
            color="white"
            size="small"
            onClick={handleScrollToTop}
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              zIndex: 1300,
              color: 'white',
              opacity: showScroll ? 1 : 0,
              pointerEvents: showScroll ? 'auto' : 'none'
            }}
            aria-label="scroll to top"
          >
            <Iconify icon="mdi:chevron-up" color="info" height={30} width={30}/>
          </Fab>
        </Fade>
      </>
    }
  </Box>
}